/**
 * @file ResearchSections.js
 * @module src/js/components/ResearchSections
 * @description
 * **Component to display and manage research sections.**
 * - Fetches research section data from the backend.
 * - Displays sections with images and content.
 * - Allows editing of sections for authenticated users.
 * - Supports adding new sections with or without images.
 * - Utilizes the modal system to show messages.
 *
 * @version
 * <p>
 *  2025-12-01: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Add an `ExternalLink` icon to the "Learn More" button. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Display messages using the modal system. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Refactor code for better readability and maintainability. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { Fragment, createRef, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useUser } from "../contexts/UserProvider";
import useFetchFromBackend from "../hooks/useFetchFromBackend";
import useUpdateProfile from '../hooks/useUpdateProfile';
import ResearchSectionContent from "./ResearchSectionContent";
import ResearchSectionButton from "./ResearchSectionButton";
import { StyledImg, NewTab, LoadingThreeDots } from "./UtilityComponents";

export default function ResearchSections() {
    const { uid } = useUser();
    const fetchFromBackend = useFetchFromBackend();
    const [showInputs, setShowInputs] = useState([]);
    const [sectionsData, setSectionsData] = useState([]);
    const [updatedImgFiles, setUpdatedImgFiles] = useState([]);
    const [updatedSectionsData, setUpdatedSectionsData] = useState([]);
    const [updating, setUpdating] = useState(false);
    const [titleRefToScrollIntoView, setTitleRefToScrollIntoView] = useState(null);
    const [isHovered, setIsHovered] = useState([false, false]);
    const [loading, setLoading] = useState(true);
    const titleRefs = useRef([]);
    const updateProfile = useUpdateProfile({
        setUpdatedProfileData: setUpdatedSectionsData,
        setUpdatedImgFiles,
        setProfileData: setSectionsData,
        setUpdating,
        setShowInputs,
    });

    const notEditingAnySection = showInputs.every((item) => item === false);
    const isGuest = uid === 'Guest';

    const handleClickCancelEdit = useCallback((index, withImage) => {
        setShowInputs(prev => prev.map((val, i) => i === index ? !val : false));
        setTitleRefToScrollIntoView(index);
        setUpdatedSectionsData(sectionsData);
        if (withImage) {
            setUpdatedImgFiles(new Array(sectionsData.length).fill(null));
        }
    }, [sectionsData, setUpdatedImgFiles]);

    useEffect(() => {
        setUpdatedSectionsData(prev =>
            prev.map((item, index) => ({
                ...item,
                photo: (showInputs[index] && updatedImgFiles[index] !== null)
                    ? updatedImgFiles[index].name
                    : item.photo,
            }))
        );
    }, [updatedImgFiles, showInputs]);

    const SortIndexOptions = (index) => (
        Array.from({ length: 99 }, (_, i) => (
            <option key={i + 1} value={i + 1}
                disabled={updatedSectionsData.some((item, j) => i + 1 === item.sortIndex && j !== index)}
            >{i + 1}</option>
        ))
    );

    useEffect(() => {
        if (titleRefToScrollIntoView === null) return;
        setTimeout(() => {
            const targetRef = titleRefs.current[titleRefToScrollIntoView];
            targetRef?.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest'
            });
            setTitleRefToScrollIntoView(null);
        }, 100);
    }, [titleRefToScrollIntoView, showInputs]);

    useEffect(() => {
        async function fetchData() {
            const data = await fetchFromBackend({ request: '/api/research' });
            setSectionsData(data.map(item => ({ ...item }))); // Deep copy to avoid reference issues
            setUpdatedSectionsData(data.map(item => ({ ...item }))); // Deep copy to avoid reference issues
            setUpdatedImgFiles(new Array(data.length).fill(null));
            setShowInputs(new Array(data.length).fill(false));
            setLoading(false);
        }
        fetchData();
    }, [fetchFromBackend]);

    useEffect(() => {
        titleRefs.current = Array.from(
            { length: sectionsData.length },
            (_, i) => titleRefs.current[i] ?? createRef()
        );
    }, [sectionsData]);

    return (
        <>
            <div id="wrapper" className="divided">
                {updatedSectionsData.map((updatedItem, index) => (
                    updatedItem.photo && (
                        <section className="spotlight style1 orient-right content-align-left image-position-center onscroll-image-fade-in"
                            key={index} ref={titleRefs.current[index]}
                        >
                            <div className="content">
                                {loading ? <LoadingThreeDots /> : (
                                    <ResearchSectionContent
                                        index={index}
                                        oneShowInput={showInputs[index]}
                                        updatedItem={updatedItem}
                                        oneSectionData={sectionsData[index]}
                                        setUpdatedSectionsData={setUpdatedSectionsData}
                                        updatedImgFiles={updatedImgFiles}
                                        setUpdatedImgFiles={setUpdatedImgFiles}
                                    >
                                        <SortIndexOptions index={index} />
                                    </ResearchSectionContent>
                                )}
                                <ResearchSectionButton
                                    index={index}
                                    oneShowInput={showInputs[index]}
                                    setTitleRefToScrollIntoView={setTitleRefToScrollIntoView}
                                    oneSectionData={sectionsData[index]}
                                    updatedItem={updatedItem}
                                    oneUpdatedImgFile={updatedImgFiles[index]}
                                    updating={updating}
                                    updateProfile={updateProfile}
                                    handleClickCancelEdit={handleClickCancelEdit}
                                />
                            </div>
                            <StyledImg
                                src={sectionsData[index].photo === 'none' ? null : sectionsData[index].photo_path}
                                style={{
                                    borderRadius: '10px',
                                    boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.2)'
                                }}
                            />
                        </section>
                    )
                ))}
                {!isGuest && notEditingAnySection && (
                    <section className="spotlight style1 orient-right content-align-left image-position-center onscroll-image-fade-in">
                        <div className="content"
                            onMouseEnter={() => setIsHovered([true, false])}
                            onMouseLeave={() => setIsHovered([false, false])}
                            style={{
                                border: isHovered[0] ? '2px dashed #aaa' : `2px dashed #ccc`,
                                backgroundColor: isHovered[0] ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease-in-out'
                            }}
                            onClick={() => {
                                updateProfile({
                                    request: '/api/research/with-image',
                                    addSection: true,
                                    from: '/',
                                    scrollTo: 'research',
                                });
                            }}
                        >
                            <h3 style={{ textTransform: 'none' }}>A new section with an image</h3>
                        </div>
                        <StyledImg style={{
                            borderRadius: '10px',
                            boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.2)',
                            width: '25%',
                            height: 'auto'
                        }}/>
                    </section>
                )}
            </div>
            <ul className="features">
                {updatedSectionsData.map((updatedItem, index) => (
                    updatedItem.photo_path === null && (
                        <Fragment key={index}>
                            {loading ? <LoadingThreeDots /> : (
                                <li className="icon solid fa-laptop" ref={titleRefs.current[index]}>
                                    <ResearchSectionContent
                                        index={index}
                                        oneShowInput={showInputs[index]}
                                        updatedItem={updatedItem}
                                        oneSectionData={sectionsData[index]}
                                        setUpdatedSectionsData={setUpdatedSectionsData}
                                        withImage={false}
                                    >
                                        <SortIndexOptions index={index} />
                                    </ResearchSectionContent>
                                </li>
                            )}
                            <ResearchSectionButton
                                index={index}
                                oneShowInput={showInputs[index]}
                                setTitleRefToScrollIntoView={setTitleRefToScrollIntoView}
                                oneSectionData={sectionsData[index]}
                                updatedItem={updatedItem}
                                updating={updating}
                                updateProfile={updateProfile}
                                handleClickCancelEdit={handleClickCancelEdit}
                                withImage={false}
                            />
                        </Fragment>
                    )
                ))}
                {!isGuest && notEditingAnySection && (
                    <li
                        onMouseEnter={() => setIsHovered([false, true])}
                        onMouseLeave={() => setIsHovered([false, false])}
                        style={{
                            border: isHovered[1] ? '2px dashed #aaa' : `2px dashed #ccc`,
                            backgroundColor: isHovered[1] ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out'
                        }}
                        onClick={() => {
                            updateProfile({
                                request: '/api/research/without-image',
                                addSection: true,
                                from: '/',
                                scrollTo: 'research',
                            });
                        }}
                    >
                        <h3 style={{ textTransform: 'none' }}>A new section without an image</h3>
                    </li>
                )}
            </ul>
            <ul className="actions special" style={{ marginTop: "2em" }}>
                <li>
                    <NewTab
                        href="https://cc.ee.ntu.edu.tw/~ywchang/EDA_lab.pdf"
                        className="button"
                        iconLocation="inside"
                    >Learn More</NewTab>
                </li>
                <li>
                    {isGuest && (
                        <Link to="/login" className="button"
                            state={{ from: '/', scrollTo: 'research' }}
                        >🔑 Log in to edit</Link>
                    )}
                </li>
            </ul>
        </>
    );
}
